import { FC } from "react";
import {
    ActivityIcon,
    CalendarIcon,
    ChartOfAccountsIcon,
    ClockIcon,
    CliIcon,
    CogwheelIcon,
    DollarCircleIcon,
    GrowthIcon,
    HomeIcon,
    ProfileIcon,
    ServicesIcon,
    WorkIcon,
} from "@icons";

export type OrgNavigationGate =
    | "closeManagement"
    | "reportTemplates"
    | "canViewActivityLog"
    | "canViewBilling"
    | "canManageApi"
    | "canOpenFirmSettings";

export type OrgNavigationShortcutId =
    | "tasks"
    | "work"
    | "activity"
    | "clients"
    | "chart-of-accounts-templates"
    | "report-templates"
    | "services"
    | "schedules"
    | "team"
    | "billing"
    | "api"
    | "settings";

interface OrgNavigationShortcutBase {
    id: OrgNavigationShortcutId;
    shortcut: string[];
    label: string;
    icon: FC;
    description: string;
    gate?: OrgNavigationGate;
}

export type OrgNavigationShortcutDefinition =
    | (OrgNavigationShortcutBase & { path: string; action?: never })
    | (OrgNavigationShortcutBase & { path?: never; action: "settings" });

/**
 * Keys shared with the workspace nav keep their letter (tasks, activity,
 * settings, chart of accounts, reporting); the rest take a letter no
 * workspace page uses, except clients, team and billing, which the org
 * command palette already advertised.
 */
export const ORG_NAVIGATION_SHORTCUTS: OrgNavigationShortcutDefinition[] = [
    {
        id: "tasks",
        shortcut: ["G", "Q"],
        path: "/clients-tasks",
        label: "Go to Actions",
        icon: WorkIcon,
        description: "Navigate to Actions page",
        gate: "closeManagement",
    },
    {
        id: "work",
        shortcut: ["G", "O"],
        path: "/clients-work",
        label: "Go to Home",
        icon: HomeIcon,
        description: "Navigate to Home page",
        gate: "closeManagement",
    },
    {
        id: "activity",
        shortcut: ["G", "Y"],
        path: "/organization-activity",
        label: "Go to Activity",
        icon: ClockIcon,
        description: "Navigate to Activity page",
        gate: "canViewActivityLog",
    },
    {
        id: "clients",
        shortcut: ["G", "C"],
        path: "/",
        label: "Go to Clients",
        icon: ActivityIcon,
        description: "Navigate to Clients page",
    },
    {
        id: "chart-of-accounts-templates",
        shortcut: ["G", "H"],
        path: "/templates/chart-of-accounts",
        label: "Go to Chart of Accounts templates",
        icon: ChartOfAccountsIcon,
        description: "Navigate to Chart of Accounts templates page",
    },
    {
        id: "report-templates",
        shortcut: ["G", "M"],
        path: "/templates/reports",
        label: "Go to Report templates",
        icon: GrowthIcon,
        description: "Navigate to Report templates page",
        gate: "reportTemplates",
    },
    {
        id: "services",
        shortcut: ["G", "E"],
        path: "/templates/services",
        label: "Go to Service templates",
        icon: ServicesIcon,
        description: "Navigate to Service templates page",
        gate: "closeManagement",
    },
    {
        id: "schedules",
        shortcut: ["G", "U"],
        path: "/templates/schedules",
        label: "Go to Service schedules",
        icon: CalendarIcon,
        description: "Navigate to Service schedules page",
        gate: "closeManagement",
    },
    {
        id: "team",
        shortcut: ["G", "T"],
        path: "/team",
        label: "Go to Team",
        icon: ProfileIcon,
        description: "Navigate to Team page",
    },
    {
        id: "billing",
        shortcut: ["G", "B"],
        path: "/organization-billing",
        label: "Go to Billing",
        icon: DollarCircleIcon,
        description: "Navigate to Billing page",
        gate: "canViewBilling",
    },
    {
        id: "api",
        shortcut: ["G", "P"],
        path: "/organization-api",
        label: "Go to API",
        icon: CliIcon,
        description: "Navigate to API page",
        gate: "canManageApi",
    },
    {
        id: "settings",
        shortcut: ["G", "S"],
        label: "Go to Settings",
        icon: CogwheelIcon,
        description: "Navigate to Settings",
        action: "settings",
        gate: "canOpenFirmSettings",
    },
];
